import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  declineCallRequest,
  endCallRequest,
  getCallRecordingUrlRequest,
  getCallRequest,
  joinCallRequest,
  leaveCallRequest,
  listCallsForThreadRequest,
  startCallRequest,
} from '../api/callsApi';
import { queryKeys } from '../query/queryKeys';

/**
 * Architecture Rule 56 + 74 — TanStack Query hooks for the Calls module.
 * All page components and the IncomingCallModal call into here so the
 * cache is shared and invalidations are centralized.
 */

/** Single-call live state (status, participants, recording). */
export function useCall(callId, options = {}) {
  return useQuery({
    queryKey: queryKeys.calls.detail(callId),
    queryFn: async () => (await getCallRequest(callId)).data?.data?.call ?? null,
    enabled: !!callId,
    refetchInterval: options.refetchInterval ?? false,
  });
}

/** Paginated call history for one thread (CallHistoryPage). */
export function useCallsForThread(threadId, params = {}) {
  return useQuery({
    queryKey: queryKeys.calls.thread(threadId, params),
    queryFn: async () =>
      (await listCallsForThreadRequest(threadId, params)).data?.data ?? { items: [], pagination: {} },
    enabled: !!threadId,
  });
}

/**
 * Rule 74 — does the thread currently have a ringing/active call?
 * Used by ChatPage's DM "Add People" affordance to disable promotion to
 * a group while a call is in flight (avoids splitting the call across
 * a defunct DM thread + a new group thread).
 */
export function useThreadHasActiveCall(threadId) {
  const q = useCallsForThread(threadId, { per_page: 5 });
  const items = Array.isArray(q.data?.items) ? q.data.items : [];
  const live = items.find((c) => c?.status === 'ringing' || c?.status === 'active') ?? null;
  return { isLoading: q.isLoading, activeCall: live, hasActiveCall: !!live };
}

/** Resolve the permanent public share URL for a completed recording. */
export function useCallRecordingUrl(callId, enabled = false) {
  return useQuery({
    queryKey: queryKeys.calls.recordingUrl(callId),
    queryFn: async () => (await getCallRecordingUrlRequest(callId)).data?.data ?? null,
    enabled: !!callId && enabled,
  });
}

/** Start a call. Returns { call, token } via onSuccess; SPA navigates to /call/{id}. */
export function useStartCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, kind = 'voice' }) =>
      startCallRequest(threadId, kind).then((r) => r.data?.data),
    onSuccess: (data) => {
      if (data?.call?.id) {
        qc.setQueryData(queryKeys.calls.detail(data.call.id), data.call);
      }
      qc.invalidateQueries({ queryKey: queryKeys.calls.all });
    },
  });
}

/** Accept an incoming call. Returns the fresh { call, token } envelope. */
export function useJoinCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId) => joinCallRequest(callId).then((r) => r.data?.data),
    onSuccess: (data, callId) => {
      if (data?.call) {
        qc.setQueryData(queryKeys.calls.detail(callId), data.call);
      }
    },
  });
}

export function useDeclineCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId) => declineCallRequest(callId).then((r) => r.data?.data),
    onSuccess: (_, callId) => qc.invalidateQueries({ queryKey: queryKeys.calls.detail(callId) }),
  });
}

export function useLeaveCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId) => leaveCallRequest(callId).then((r) => r.data?.data),
    onSuccess: (_, callId) => qc.invalidateQueries({ queryKey: queryKeys.calls.detail(callId) }),
  });
}

export function useEndCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId) => endCallRequest(callId).then((r) => r.data?.data),
    onSuccess: (_, callId) => qc.invalidateQueries({ queryKey: queryKeys.calls.detail(callId) }),
  });
}
